import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Truck, Calendar, MoreVertical, Pencil, Trash2 } from 'lucide-react';
import { Vehicle } from '@/hooks/useVehicles';
import { format } from 'date-fns';
import { fr, enUS, es, de, it } from 'date-fns/locale';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DocumentStatusBadge } from './DocumentStatusBadge';

interface VehicleCardProps {
  vehicle: Vehicle;
  onEdit: (vehicle: Vehicle) => void;
  onDelete: (vehicleId: string) => void;
}

export const VehicleCard = ({ vehicle, onEdit, onDelete }: VehicleCardProps) => {
  const { t, i18n } = useTranslation();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const getDateLocale = () => {
    switch (i18n.language) {
      case 'fr': return fr;
      case 'es': return es;
      case 'de': return de;
      case 'it': return it;
      default: return enUS;
    }
  };

  const getStatusBadge = () => {
    if (vehicle.is_available) {
      return (
        <Badge className="bg-[#14A38B]/10 text-[#14A38B] border-[#14A38B]/20 font-['Funnel_Sans']">
          {t('fleet.vehicleDetails.available')}
        </Badge>
      );
    }
    return (
      <Badge className="bg-[#F2AC57]/10 text-[#F2AC57] border-[#F2AC57]/20 font-['Funnel_Sans']">
        {t('fleet.vehicleDetails.onMission')}
      </Badge>
    );
  };

  return (
    <>
      <Card className="border border-[#DBE2EA] rounded-xl hover:shadow-md hover:border-[#0880AE] transition-all duration-300">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#0880AE]/10 rounded-lg flex items-center justify-center">
                <Truck className="text-[#0880AE]" size={20} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-[#37699E] font-['Lexend_Deca']">
                  {vehicle.plate_number}
                </h3>
                <p className="text-xs text-[#5A6B7A] font-['Funnel_Sans']">
                  {vehicle.vehicle_types?.name || 'N/A'}
                </p>
              </div>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-[#5A6B7A] hover:bg-[#EBF4F8]"
                >
                  <MoreVertical size={18} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 bg-white z-50">
                <DropdownMenuItem
                  onClick={() => onEdit(vehicle)}
                  className="cursor-pointer font-['Funnel_Sans']"
                >
                  <Pencil size={16} className="mr-2 text-[#0880AE]" />
                  {t('fleet.actions.edit')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setShowDeleteDialog(true)}
                  className="cursor-pointer text-[#FF7171] focus:text-[#FF7171] font-['Funnel_Sans']"
                >
                  <Trash2 size={16} className="mr-2" />
                  {t('fleet.actions.delete')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            {getStatusBadge()}
            <DocumentStatusBadge
              verifiedAt={vehicle.registration_verified_at}
              expiry={vehicle.registration_expiry}
              label={t('fleet.documentStatus.registrationCard')}
            />
          </div>
          {(vehicle.brand || vehicle.model) && (
            <p className="text-sm text-[#2D3748] font-['Funnel_Sans']">
              <span className="font-medium">{t('fleet.vehicleDetails.modelLabel')}</span>{' '}
              {[vehicle.brand, vehicle.model, vehicle.year].filter(Boolean).join(' ')}
            </p>
          )}
          {vehicle.vin && (
            <p className="text-sm text-[#2D3748] font-['Funnel_Sans']">
              <span className="font-medium">{t('fleet.vehicleDetails.vin')}</span> {vehicle.vin}
            </p>
          )}
          {vehicle.next_maintenance_date && (
            <div className="flex items-center gap-2 pt-2 border-t border-[#DBE2EA]">
              <Calendar className="text-[#5A6B7A]" size={14} />
              <p className="text-xs text-[#5A6B7A] font-['Funnel_Sans']">
                {t('fleet.vehicleDetails.nextMaintenanceLabel')} {format(new Date(vehicle.next_maintenance_date), 'dd MMM yyyy', { locale: getDateLocale() })}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#37699E] font-['Lexend_Deca']">
              {t('fleet.deleteDialog.vehicleTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription className="font-['Funnel_Sans']">
              {t('fleet.deleteDialog.vehicleDescription')} <strong>{vehicle.plate_number}</strong> ?
              {' '}{t('fleet.deleteDialog.irreversible')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#DBE2EA] font-['Funnel_Sans']">
              {t('fleet.actions.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                onDelete(vehicle.id);
                setShowDeleteDialog(false);
              }}
              className="bg-[#FF7171] hover:bg-[#E65959] text-white font-['Funnel_Sans']"
            >
              {t('fleet.actions.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};